import React, { useCallback, useState } from 'react';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { GameObject, Transform, DEFAULT_TRANSFORM } from '../../../core/types/gameobject.types';
import { useSceneStore } from '../../store/sceneStore';
import { Vector2Input, NumberInput } from './PropertyEditors';
import { cn } from '../../utils/cn';

interface TransformSectionProps {
  gameObject: GameObject;
}

export const TransformSection: React.FC<TransformSectionProps> = ({ gameObject }) => {
  const { updateTransform } = useSceneStore();
  const [isExpanded, setIsExpanded] = useState(true);

  const handlePositionChange = useCallback(
    (position: { x: number; y: number }) => {
      updateTransform(gameObject.id, { position });
    },
    [gameObject.id, updateTransform]
  );

  const handleRotationChange = useCallback(
    (rotation: number) => {
      updateTransform(gameObject.id, { rotation });
    },
    [gameObject.id, updateTransform]
  );

  const handleScaleChange = useCallback(
    (scale: { x: number; y: number }) => {
      updateTransform(gameObject.id, { scale });
    },
    [gameObject.id, updateTransform]
  );

  const handleAnchorChange = useCallback(
    (anchor: { x: number; y: number }) => {
      updateTransform(gameObject.id, { anchor });
    },
    [gameObject.id, updateTransform]
  );

  const handleSortOrderChange = useCallback(
    (sortOrder: number) => {
      updateTransform(gameObject.id, { sortOrder });
    },
    [gameObject.id, updateTransform]
  );

  const handleReset = useCallback(() => {
    updateTransform(gameObject.id, { ...DEFAULT_TRANSFORM });
  }, [gameObject.id, updateTransform]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  return (
    <div className="border-b border-zinc-800">
      {/* Header */}
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 cursor-pointer',
          'hover:bg-zinc-800/50 transition-colors'
        )}
        onClick={toggleExpanded}
      >
        <button className="p-0.5 text-zinc-400 hover:text-zinc-200">
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span className="text-sm font-medium text-zinc-200 select-none">Transform</span>
        <div className="flex-1" />
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleReset();
          }}
          className={cn(
            'p-1 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 rounded',
            'transition-colors'
          )}
          title="Reset Transform"
        >
          <RotateCcw size={12} />
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3">
          {/* Position */}
          <div className="space-y-1">
            <label className="text-xs text-zinc-500">Position</label>
            <Vector2Input
              value={gameObject.transform.position}
              onChange={handlePositionChange}
              step={1}
            />
          </div>

          {/* Rotation */}
          <div className="space-y-1">
            <label className="text-xs text-zinc-500">Rotation</label>
            <NumberInput
              value={gameObject.transform.rotation}
              onChange={handleRotationChange}
              min={-360}
              max={360}
              step={1}
            />
          </div>

          {/* Scale */}
          <div className="space-y-1">
            <label className="text-xs text-zinc-500">Scale</label>
            <Vector2Input
              value={gameObject.transform.scale}
              onChange={handleScaleChange}
              step={0.1}
              showLockRatio
            />
          </div>

          {/* Anchor */}
          <div className="space-y-1">
            <label className="text-xs text-zinc-500">Anchor</label>
            <Vector2Input
              value={gameObject.transform.anchor}
              onChange={handleAnchorChange}
              min={0}
              max={1}
              step={0.1}
            />
          </div>

          {/* Sort Order */}
          <div className="space-y-1">
            <label className="text-xs text-zinc-500">Sort Order</label>
            <NumberInput
              value={gameObject.transform.sortOrder ?? 0}
              onChange={handleSortOrderChange}
              step={1}
            />
          </div>
        </div>
      )}
    </div>
  );
};
